/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// function handleMobileLayout() {
//     const isMobile = window.innerWidth < 1000; // Adjust this breakpoint as needed
//     const facetFilters = document.getElementById('facet-filters');
//     const productFacetAsideInner = document.getElementById('product-facet__aside-inner');
//
//     if (isMobile) {
//         if (facetFilters.parentElement !== document.body) {
//             document.body.appendChild(facetFilters);
//         }
//     } else {
//         if (facetFilters.parentElement !== productFacetAsideInner) {
//             productFacetAsideInner.insertBefore(facetFilters, productFacetAsideInner.firstChild);
//         }
//     }
// }
//
// // Initial check
// handleMobileLayout();
//
// // Check on window resize
// window.addEventListener('resize', handleMobileLayout);

const addToCart = (body, reload = true, autoclose = false, openMinicart = true) => updateCart('add', body, reload, autoclose, openMinicart);
const changeCart = (body, reload = true, autoclose = false) => updateCart('change', body, reload, autoclose);
const updateCart = async (type, body, reload = true, autoclose = false, openMinicart = true) => {
    const url = type === 'add' ? window.themeVariables.routes.cartAddUrl : window.themeVariables.routes.cartChangeUrl;

    if (reload) {
        body.sections = body.sections || [];
        if (!body.sections.includes("mini-cart")) {
            body.sections.push("mini-cart");
        }
    }

    try {
        const response = await fetch(`${url}.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const cartContent = await response.json();
        if (reload) {
            reloadMiniCart(cartContent, autoclose, openMinicart);
        }
    } catch (error) {
        console.error('Error updating cart:', error);
    }
};

const reloadMiniCart = (cartContent, autoclose, openMinicart) => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
    }));
    if (openMinicart) {
        console.log('open minicart');
        document.documentElement.dispatchEvent(new CustomEvent('multidrawer:open', {
            detail: {tab: 'minicart',}
        }));
    }
    if (autoclose) {
        setTimeout(() => {
            document.documentElement.dispatchEvent(new CustomEvent('multidrawer:close'));
        }, 3000);
    }
};


// Load more button
document.addEventListener('DOMContentLoaded', function () {
    const loadMoreButton = document.querySelector('.load-more-products');

    if (loadMoreButton) {
        loadMoreButton.addEventListener('click', function () {
            const productsOnPage = document.querySelector('.products-on-page');
            let nextUrl = productsOnPage.dataset.nextUrl;
            const loadMoreBtn = loadMoreButton.querySelector('.button');
            const loadMoreSpinner = document.querySelector('.load-more-spinner');

            fetch(nextUrl)
                .then(response => response.text())
                .then(html => {
                    const parser = new DOMParser();
                    const nextPage = parser.parseFromString(html, 'text/html');

                    loadMoreSpinner.style.display = 'none';
                    const newProducts = nextPage.querySelector('.products-on-page');
                    newProducts.querySelectorAll('.product-item').forEach(product => {
                        product.style.opacity = '1';
                    })
                    const newUrl = newProducts.dataset.nextUrl;

                    if (newUrl) {
                        loadMoreBtn.style.display = 'block';
                    } else {
                        loadMoreBtn.style.display = 'none';
                    }

                    nextUrl = newUrl;
                    productsOnPage.dataset.nextUrl = nextUrl;

                    productsOnPage.insertAdjacentHTML('beforeend', newProducts.innerHTML);

                    const currentProducts = productsOnPage.querySelectorAll('.product-item').length;
                    document.querySelector('.current-product-count').textContent = currentProducts;
                })
                .catch(error => {
                    console.error('Error:', error);
                    loadMoreSpinner.style.display = 'none';
                    loadMoreBtn.style.display = 'block';
                });

            loadMoreBtn.style.display = 'none';
            loadMoreSpinner.style.display = 'block';
        });
    }
});
let lastScrollTop = 0;
let scrollTimer;

function handleHeaderScroll() {
    const header = document.querySelector('store-header.header');
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const collectionPage = document.body.classList.contains('page--collection');
    const submenuEl = document.querySelector('.page-submenu');
    const headerHeight = document.querySelector('store-header')?.offsetHeight;

    // Add or remove scroll direction classes
    if (scrollTop > lastScrollTop) {
        header.classList.remove('scroll-up');
        header.classList.add('scroll-down');
        submenuEl?.classList.remove('is-fixed');

        if (collectionPage) {
            const filterBarMobile = document.querySelector('#mobile-facet-toolbar');
            filterBarMobile.classList.remove('is-fixed');
        }
    } else if (scrollTop < lastScrollTop) {
        header.classList.remove('scroll-down');
        header.classList.add('scroll-up');
        submenuEl?.classList.add('is-fixed');

        if (collectionPage) {
            const filterBarMobile = document.querySelector('#mobile-facet-toolbar');
            filterBarMobile.classList.add('is-fixed');
        }
    }

    const submenuTop = submenuEl?.getBoundingClientRect().top;
    if (submenuTop <= headerHeight) {
        submenuEl?.classList.add('borderless');
    } else {
        submenuEl?.classList.remove('borderless');
    }

    lastScrollTop = scrollTop;

    // Clear previous timer
    clearTimeout(scrollTimer);

    // Remove fixed class if it exists
    header.classList.remove('header-fixed');

    // Set a new timer to add the fixed class after scrolling ends
    scrollTimer = setTimeout(() => {
        header.classList.add('header-fixed');
        submenuEl?.classList.add('is-fixed');
    }, 1000);
}

// Event listener for animation or transition end on the header element
document.querySelector('store-header.header')?.addEventListener('transitionend', () => {
    const header = document.querySelector('store-header.header');
    if (!header.classList.contains('header-fixed')) {
        header.classList.add('header-fixed');
    }
});

// Attach the scroll event listener
window.addEventListener('scroll', handleHeaderScroll);


(function () {
    // Store the original dispatchEvent method
    const originalDispatchEvent = EventTarget.prototype.dispatchEvent;

    // Override the dispatchEvent method
    EventTarget.prototype.dispatchEvent = function (event) {
        console.log(`Event dispatched: ${event.type}`, {
            event: event,
            element: this
        });

        originalDispatchEvent.call(this, event);
    };
})();


// Fix button add to cart product page mobile
document.addEventListener('DOMContentLoaded', () => {
    const addToCartButton = document.querySelector('.add-to-cart-mobile');
    let originalPositionTop;

    function setOriginalPosition() {
        originalPositionTop = addToCartButton?.getBoundingClientRect().top + window.scrollY;
    }

    function isOriginalPositionVisible() {
        const currentViewportTop = window.scrollY;
        const currentViewportBottom = currentViewportTop + window.innerHeight;

        return originalPositionTop >= currentViewportTop && originalPositionTop <= currentViewportBottom;
    }

    function toggleButtonPosition() {
        if (!addToCartButton) {
            return;
        }
        if (isOriginalPositionVisible()) {
            addToCartButton.classList.remove('fixed');
        } else {
            addToCartButton.classList.add('fixed');
        }
    }

    setOriginalPosition();

    // Event listeners for scrolling and resizing
    window.addEventListener('scroll', toggleButtonPosition);
    window.addEventListener('resize', () => {
        setOriginalPosition(); // Recalculate original position on resize
        toggleButtonPosition(); // Apply logic after recalculating
    });

    // Initial check
    toggleButtonPosition();
});
